import { Grid } from "@material-ui/core";
import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { FC, Fragment } from "react";
import { Datagrid, ExportButton, FunctionField, NumberField, TextField, TopToolbar, useListContext } from "react-admin";
import { DefaultPerPageSize } from "../../../Constants/Constant";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";
import { SourceReliabilityNffReport } from "./Constants";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import { FilterInputType } from "../../../Enums/FilterTypes";

const useFooterStyles = makeStyles(theme => ({
  footerContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderTop: "2px solid #ddd",
    marginTop: theme.spacing(1),
    gap: theme.spacing(3),
  },
  totalItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  totalLabel: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#666",
  },
  totalValue: {
    fontWeight: 700,
    fontSize: "16px",
    color: "#333",
  },
  nffRateValue: {
    fontWeight: 700,
    fontSize: "16px",
    color: "#d32f2f",
  },
}));

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const { data, ids } = useListContext();
  const footerClasses = useFooterStyles();

  const calculateTotals = () => {
    if (!data || !ids || ids.length === 0) {
      return { totalUnscheduledRemovals: 0, totalCompletedRemovals: 0, totalNff: 0, totalNffRate: "0.00" };
    }

    const records = ids.map(id => data[id]).filter(Boolean);
    const totalUnscheduledRemovals = records.reduce((sum, item) => sum + (Number(item.unscheduledRemovals) || 0), 0);
    const totalCompletedRemovals = records.reduce((sum, item) => sum + (Number(item.completedRemovals) || 0), 0);
    const totalNff = records.reduce((sum, item) => sum + (Number(item.nff) || 0), 0);
    const totalNffRate = totalCompletedRemovals > 0 ? ((totalNff / totalCompletedRemovals) * 100).toFixed(2) : "0.00";

    return { totalUnscheduledRemovals, totalCompletedRemovals, totalNff, totalNffRate };
  };

  const TotalsFooter = () => {
    const totals = calculateTotals();

    return (
      <Box className={footerClasses.footerContainer}>
        <Box className={footerClasses.totalItem}>
          <Typography className={footerClasses.totalLabel}>Total Unscheduled Removals:</Typography>
          <Typography className={footerClasses.totalValue}>{totals.totalUnscheduledRemovals}</Typography>
        </Box>
        <Box className={footerClasses.totalItem}>
          <Typography className={footerClasses.totalLabel}>Total Completed Removals:</Typography>
          <Typography className={footerClasses.totalValue}>{totals.totalCompletedRemovals}</Typography>
        </Box>
        <Box className={footerClasses.totalItem}>
          <Typography className={footerClasses.totalLabel}>Total NFF:</Typography>
          <Typography className={footerClasses.totalValue}>{totals.totalNff}</Typography>
        </Box>
        <Box className={footerClasses.totalItem}>
          <Typography className={footerClasses.totalLabel}>NFF Rate:</Typography>
          <Typography className={footerClasses.nffRateValue}>%{totals.totalNffRate}</Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid optimized basePath={SourceReliabilityNffReport}>
        <TextField source="customer" label="Customer" />
        <TextField source="pn" label="Pn" />
        <TextField source="fleetType" label="Fleet Type" />
        <TextField source="pnDescription" label="Pn Description" />
        <TextField source="pnCategory" label="Pn Category" />
        <TextField source="pnSubcategory" label="Pn Sub Category" />
        <TextField source="chapter" label="Chapter" />
        <NumberField source="unscheduledRemovals" label="Unscheduled Removals" />
        <NumberField source="completedRemovals" label="Completed Removals" />
        <NumberField source="nff" label="NFF" />
        <FunctionField
          source="nffRate"
          label="NFF Rate"
          render={(record: any) => (
            <span style={{ color: "#d32f2f", fontWeight: 500 }}>%{record?.nffRate?.toFixed(2) || "0.00"}</span>
          )}
        />
      </Datagrid>
      <TotalsFooter />
    </Fragment>
  );
};

const NffReportList = props => {
  //useVerifyPermission("customroutes", "reliabilityfailonfits.read");

  const ListActions = props => {
    return (
      <TopToolbar {...props}>
        <ExportButton
          resource={props.resource}
          sort={props.currentSort}
          filterValues={props.filterValues}
          maxResults={10000}
        />
      </TopToolbar>
    );
  };

  const exporter = records => {
    const data = records.map(record => ({
      Customer: record.customer,
      Pn: record.pn,
      "Fleet Type": record.fleetType,
      "Pn Description": record.pnDescription,
      "Pn Category": record.pnCategory,
      "Pn Sub Category": record.pnSubcategory,
      Chapter: record.chapter,
      "Unscheduled Removals": record.unscheduledRemovals,
      "Completed Removals": record.completedRemovals,
      NFF: record.nff,
      "NFF Rate": record.nffRate,
    }));

    const columns = Object.keys(data[0]).map(record => ({ header: record }));

    ExcelExport(columns, data, "NFF Report List");
  };

  const filterDataSource = [
    { source: "customer", inputType: FilterInputType.Text, label: "Customer" },
    { source: "pn", inputType: FilterInputType.Text, label: "Pn" },
    { source: "pnDescription", inputType: FilterInputType.Text, label: "Pn Description" },
    { source: "pnCategory", inputType: FilterInputType.Text, label: "Pn Category" },
    { source: "chapter", inputType: FilterInputType.Text, label: "Chapter" },
    { source: "nffRate", inputType: FilterInputType.Number, label: "NFF Rate (%)" },
    { source: "nff", inputType: FilterInputType.Number, label: "NFF" },
    { source: "unscheduledRemovals", inputType: FilterInputType.Number, label: "Unscheduled Removals" },
    { source: "completedRemovals", inputType: FilterInputType.Number, label: "Completed Removals" },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <PoolList
          {...props}
          hasCreate={false}
          exporter={exporter}
          actions={<ListActions />}
          bulkActionButtons={false}
          resource={SourceReliabilityNffReport}
          title={"Reliability Nff Report List"}
          sort={{ field: "nffRate", order: "DESC" }}
          perPage={DefaultPerPageSize}
          filters={<FilterToolbar dataSource={filterDataSource} />}
          filter={props?.filterParams}
        >
          <TabbedDatagrid />
        </PoolList>
      </Grid>
    </Grid>
  );
};

export default NffReportList;
